// src/constants/theme.js
// Central design tokens — "Brian UI 1.0" (Figma frame 1.0, 1920×1080).
// Single source of truth for the restyle: the light `t` branch in
// EscContentCreatorPro.jsx and the shell (Sidebar/Header) both import from here.
// Full spec: plans/brian-ui-1.0-spec.md. Rule: use ONLY these tokens — don't invent.

export const COLORS = {
  // Brand
  accent:        '#7843FF',                 // accento — primary btns, active nav, selected, slider fill, links
  accentSoft:    'rgba(120,67,255,0.10)',   // accento-soft — selected content-type card bg
  accentTint:    '#E7DEFF',                 // viola B — avatar badge / documents source tile
  ink:           '#23003E',                 // nero — sidebar bg, primary text, headings, icon strokes
  bg:            '#F0EEF2',                  // sfondo — app/page background

  // Neutrals
  surface:       '#FFFFFF',                 // superficie — card surfaces, text on accent
  field:         '#F5F4F6',                 // campi — input/segmented-track/header-icon bg
  border:        '#EBE9ED',                 // bordo — default card/field borders, toggle-off track
  borderStrong:  '#D4CFD9',                 // bordo-marcato — emphasized borders, muted counters
  textSecondary: '#7F738C',                 // testo-secondario — unselected card labels/icons
  textTertiary:  '#A299AD',                 // testo-terziario — helper/placeholder/inactive/sidebar-muted

  // Field border == default border in 1.0 (kept as its own key for the light `t` mapping)
  inputBorder:   '#EBE9ED',
};

// Semantic pairs (fg = icon/text, bg = tile/badge background) — Fonti source icons, badges, help icons
export const SEMANTIC = {
  viola:      { fg: '#7843FF', bg: '#E7DEFF' }, // Documenti / URL badge / avatar
  verde:      { fg: '#54A26B', bg: '#D6EBDC' }, // Knowledge Base
  attenzione: { fg: '#F4A825', bg: '#F9EEDC' }, // URL di riferimento
  info:       { fg: '#3586E9', bg: '#DCE9F9' }, // Ricerca web / help (?) icons
  errore:     { fg: '#F95340', bg: '#FCE3E0' }, // required asterisk / validation (bg ≈ "Errore B" — verify exact hex in Figma)
  // Neutral pair — badges/pills for unknown or inert states (run status fallback,
  // "n.d." chips). Not in the Figma frame; derived from the neutral ramp so it
  // reads as the same family as the five semantic pairs above.
  neutro:     { fg: '#7F738C', bg: '#F5F4F6' },
};

// Darker step of the verde hue. Needed only where two positive states must be
// told apart inside one chart (completed vs approved) — same hue, more depth, so
// the pair still reads as "both good". Not a Figma token; derived.
export const VERDE_DARK = '#3D7A50';

// Corner radii (px). Scale per the kit's "Tipografia & geometrie" panel: 8/10/12/24/36.
export const RADII = {
  section:      '36px', // section cards, summary panel
  source:       '24px', // Fonti source rows + badge pills
  card:         '12px', // content-type selector cards (was 14 — off the 8/10/12/24/36 scale)
  control:      '12px', // buttons, inputs, nav items, icon tiles, segmented track
  controlInner: '8px',  // segmented-control active thumb (was 9 — off scale)
  pill:         '999px',// toggles, sliders, circular borders, progress bars, avatar
  mark:         '3px',  // inline highlight of a word inside running text — smaller than any control radius so it doesn't read as a pill
};

export const SHADOW = {
  card:    '0px 1px 2px rgba(40, 45, 20, 0.04)',
  // Floating menus (the history card's download menu). Not a Figma token:
  // derived from `ink` (#23003E) at 12% alpha so the lift reads in the brand hue.
  popover: '0px 8px 24px rgba(35, 0, 62, 0.12)',
};

// ── Charts ───────────────────────────────────────────────────────────────────
// Categorical series palette for the Dashboard charts. Rooted in the Brian UI
// hues (accento, verde, errore, info) and extended with four further hues, then
// checked against the six palette rules: lightness band, chroma floor, adjacent
// CVD separation, normal-vision floor, and ≥3:1 contrast on a light surface.
//
// Assign in FIXED ORDER, never cycled — a series keeps its colour when filters
// change the series count, and a 9th series folds into "Altro" rather than
// wrapping back to slot 1.
//
// Known limit: slots 6↔7 (#069E92 ↔ #B5399A) sit at ΔE 7.8 under deuteranopia —
// inside the 6–8 floor band, which is legal only because every multi-series chart
// here ships a legend, so identity is never carried by colour alone.
export const CHART_SERIES = [
  '#7843FF', // accento
  '#54A26B', // verde
  '#F95340', // errore
  '#3586E9', // info
  '#C97F0A', // ambra scura (attenzione, scurita per il contrasto su superficie chiara)
  '#069E92', // teal
  '#B5399A', // magenta
  '#7A9A1E', // oliva
];

// Chart chrome — axes, gridlines and labels stay recessive so the marks lead.
export const CHART = {
  grid:  COLORS.border,
  axis:  COLORS.textTertiary,
  label: COLORS.textSecondary,
  value: COLORS.ink,
};

// Typeface split per the kit: Display for large/bold headings, Text for body/small.
// FONT_SANS stays = Display for backward compatibility; a deliberate per-element
// pass will migrate body copy to FONT_TEXT (see review notes).
export const FONT_DISPLAY = "'Wix Madefor Display', system-ui, sans-serif";
export const FONT_TEXT    = "'Wix Madefor Text', system-ui, sans-serif";
export const FONT_SANS    = FONT_DISPLAY;

// Monospace — kept ONLY where the fixed advance carries meaning: run/user IDs,
// hashes, JSON payloads, page counters. Never for prose or UI chrome.
export const FONT_MONO    = "'JetBrains Mono', ui-monospace, monospace";

// ── Legacy → token mapping ───────────────────────────────────────────────────
// Reference for the frontend harmonization sweep. Every value on the left came
// from the pre-restyle theme (ESC red accent + olive/slate neutrals + raw
// Tailwind status hexes) and is being replaced by the token on the right.
// Kept as a comment, not code: nothing should import the legacy values.
//
//   #E8002D / #FF0037  (old ESC accent)  → COLORS.accent
//   #6366F1 / #8B5CF6  (indigo/violet)   → COLORS.accent
//   #1D1D1F / #1C1D17  (primary text)    → COLORS.ink
//   #3A3A3C            (dark grey text)  → COLORS.ink
//   #6C6C6D / #82857A  (secondary text)  → COLORS.textSecondary
//   #A6A99C / #9CA3AF  (muted text)      → COLORS.textTertiary
//   #F5F5F7 / #FDFDFB  (field/tint bg)   → COLORS.field
//   rgba(0,0,0,0.05..0.08)  (hairlines)  → COLORS.border
//   rgba(0,0,0,0.10..0.12)  (borders)    → COLORS.borderStrong
//   #EF4444            (error)           → SEMANTIC.errore.fg
//   #10B981 / #10A37F  (success)         → SEMANTIC.verde.fg
//   #F59E0B / #B45309  (warning)         → SEMANTIC.attenzione.fg
//   #3B82F6            (info)            → SEMANTIC.info.fg
//   'DM Sans'                            → FONT_SANS
//   'JetBrains Mono'                     → FONT_MONO
//
// Platform brand colors in components/outputs/ (LinkedIn #0A66C2, Instagram
// #E4405F, YouTube #FF0000, …) are deliberately NOT mapped — they identify the
// channel rather than express the product's visual language.
